import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { X, LifeBuoy, Clock, Zap, ChefHat, ExternalLink } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { MillieAvatar } from "../components/MascotBubble";
import { TAKEAWAY_IMG } from "../lib/millie";
import DishImage from "../components/DishImage";

const TIMES = [10, 15, 20, 30];
const MOODS = [
  { key: "exhausted", label: "Wiped out" },
  { key: "no time", label: "No time" },
  { key: "empty fridge", label: "Bare fridge" },
  { key: "picky kids", label: "Picky kids" },
];

export default function SOSDinner() {
  const { activeId, activeHousehold } = useAuth();
  const navigate = useNavigate();
  const [opening, setOpening] = useState(null);
  const cookIt = async (m, i) => {
    if (opening !== null) return;
    setOpening(i);
    try { const { data } = await api.post("/sos/cook", { household_id: activeId, meal: m }); navigate(`/cook/sos/${data.sos_id}`); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); setOpening(null); }
  };
  const [maxTime, setMaxTime] = useState(20);
  const [mood, setMood] = useState("exhausted");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const cur = activeHousehold?.currency || "A$";

  const rescue = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/sos", { household_id: activeId, max_time: maxTime, mood });
      setResult(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#2B2233] flex justify-center">
      <div className="relative w-full max-w-md min-h-screen bg-warning overflow-hidden">
        {/* header */}
        <div className="px-6 pt-12 pb-6">
          <button onClick={() => navigate("/home")} data-testid="sos-cancel" aria-label="Cancel SOS"
            className="w-11 h-11 rounded-full bg-white/25 backdrop-blur flex items-center justify-center text-white">
            <X size={22} strokeWidth={2.5} />
          </button>

          <div className="mt-6 flex items-center gap-3">
            <MillieAvatar size={64} mood={loading ? "thinking" : result ? "celebrate" : "surprised"} />
            <div className="text-white">
              <div className="flex items-center gap-2">
                <LifeBuoy size={22} strokeWidth={2.5} />
                <h1 className="font-heading text-3xl font-bold">SOS Dinner</h1>
              </div>
              <p className="font-semibold text-white/90 text-sm mt-0.5">Breathe. I've got you. Let's rescue tonight.</p>
            </div>
          </div>
        </div>

        {/* controls */}
        <div className="bg-background rounded-t-[2.5rem] min-h-[70vh] px-6 pt-8 pb-32">
          {!result ? (
            <div className="space-y-7">
              <div>
                <p className="font-heading text-lg font-semibold text-sorted-ink flex items-center gap-2 mb-3">
                  <Clock size={18} strokeWidth={2.5} /> How long have you got?
                </p>
                <div className="flex gap-2">
                  {TIMES.map((t) => (
                    <button key={t} onClick={() => setMaxTime(t)} data-testid={`sos-time-${t}`}
                      className={`flex-1 py-4 rounded-2xl font-bold border-2 transition-colors ${maxTime === t ? "bg-warning border-warning text-white" : "bg-white border-[#FFF3F0] text-sorted-ink"}`}>
                      {t}m
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <p className="font-heading text-lg font-semibold text-sorted-ink flex items-center gap-2 mb-3">
                  <Zap size={18} strokeWidth={2.5} /> What's going on?
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {MOODS.map((m, i) => (
                    <button key={m.key} onClick={() => setMood(m.key)} data-testid={`sos-mood-${m.key}`}
                      className={`py-4 rounded-2xl font-bold border-2 transition-colors ${mood === m.key ? "bg-warning border-warning text-white" : "bg-white border-[#FFF3F0] text-sorted-ink"}`}>
                      {m.label}
                    </button>
                  ))}
                </div>
              </div>

              <motion.button
                whileTap={{ scale: 0.97 }}
                onClick={rescue}
                disabled={loading}
                data-testid="sos-rescue"
                className="w-full bg-warning hover:bg-warning-hover text-white font-heading font-bold text-lg py-5 rounded-full flex items-center justify-center gap-2 shadow-floating disabled:opacity-70"
              >
                <LifeBuoy size={22} strokeWidth={2.5} className={loading ? "animate-spin" : ""} />
                {loading ? "Finding a rescue…" : "Rescue my dinner"}
              </motion.button>
            </div>
          ) : (
            <AnimatePresence>
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6" data-testid="sos-results">
                <div>
                  <h2 className="font-heading text-xl font-semibold text-sorted-ink mb-1">Raid the pantry 🧑‍🍳</h2>
                  <p className="text-sm text-sorted-slate mb-3">Using what you've already got, under {maxTime} min.</p>
                  <div className="space-y-3">
                    {result.pantry_raid.map((m, i) => (
                      <motion.div key={i} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
                        onClick={() => cookIt(m, i)} role="button" tabIndex={0}
                        className={`bg-white rounded-3xl border-2 border-[#FFF3F0] shadow-soft overflow-hidden flex cursor-pointer active:scale-[0.98] transition-transform ${opening === i ? "opacity-70" : ""}`} data-testid={`sos-pantry-${i}`}>
                        <DishImage meal={m} className="w-24 self-stretch min-h-[96px]" testId={`sos-image-${i}`} />
                        <div className="p-3 flex-1">
                          <h3 className="font-heading font-semibold text-sorted-ink leading-tight">{m.title}</h3>
                          <p className="text-xs text-sorted-slate mt-1 line-clamp-2">{m.description}</p>
                          <div className="flex items-center justify-between mt-2">
                            <span className="inline-flex items-center gap-1 text-xs font-bold text-sorted-slate">
                              <Clock size={12} strokeWidth={2.5} /> {m.prep_time_min} min
                            </span>
                            <span className="inline-flex items-center gap-1 text-xs font-bold text-sorted-rose" data-testid={`sos-cook-${i}`}>
                              {opening === i ? "Opening…" : "Cook with Millie"} <ChefHat size={13} strokeWidth={2.5} />
                            </span>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>

                <div>
                  <h2 className="font-heading text-xl font-semibold text-sorted-ink mb-1">Or let someone else cook 🛵</h2>
                  <p className="text-sm text-sorted-slate mb-3">Millie's picks for your crew — tap to open them near you.</p>
                  <div className="space-y-3">
                    {result.takeaway.map((t, i) => (
                      <div key={i} className="bg-white rounded-3xl border-2 border-[#FFF3F0] shadow-soft p-3" data-testid={`sos-takeaway-${i}`}>
                        <div className="flex items-center gap-3">
                          <img src={TAKEAWAY_IMG} alt={t.cuisine} className="w-14 h-14 rounded-2xl object-cover" />
                          <div className="flex-1 min-w-0">
                            <h3 className="font-heading font-semibold text-sorted-ink leading-tight">{t.cuisine}</h3>
                            <p className="text-xs text-sorted-slate">{t.order}</p>
                            <p className="text-[11px] text-sorted-slate mt-0.5">~{cur}{Number(t.price_estimate).toFixed(2)}{t.note ? ` · ${t.note}` : ""}</p>
                          </div>
                        </div>
                        <div className="mt-3 grid grid-cols-3 gap-2">
                          <a href={t.links?.ubereats} target="_blank" rel="noreferrer" data-testid={`sos-ubereats-${i}`}
                            className="bg-[#06C167] text-white text-xs font-bold py-2.5 rounded-full flex items-center justify-center gap-1">Uber Eats <ExternalLink size={12} strokeWidth={2.5} /></a>
                          <a href={t.links?.doordash} target="_blank" rel="noreferrer" data-testid={`sos-doordash-${i}`}
                            className="bg-[#FF3008] text-white text-xs font-bold py-2.5 rounded-full flex items-center justify-center gap-1">DoorDash <ExternalLink size={12} strokeWidth={2.5} /></a>
                          <a href={t.links?.menulog} target="_blank" rel="noreferrer" data-testid={`sos-menulog-${i}`}
                            className="bg-[#FF8000] text-white text-xs font-bold py-2.5 rounded-full flex items-center justify-center gap-1">Menulog <ExternalLink size={12} strokeWidth={2.5} /></a>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <button onClick={() => setResult(null)} data-testid="sos-again"
                  className="w-full bg-white border-2 border-[#FFF3F0] text-sorted-ink font-bold py-4 rounded-full">
                  Try different options
                </button>
              </motion.div>
            </AnimatePresence>
          )}
        </div>
      </div>
    </div>
  );
}
